import { useCallback, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormatListBulletedOutlined, Preview } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { FormPageMode } from '@practium/ui';
import { PlanForm } from '../../../system/components/Forms/PlanForm/PlanForm';
import type { PlanFormRef } from '../../../system/components/Forms/PlanForm/PlanForm';
import type { PlanFormData } from '../../../system/types/system.types';
import { PlanPreviewCard } from '../components/Preview/PlanPreviewCard/PlanPreviewCard';
import { removeLandingPlan } from '../services/landingPlan.service';
import { usePermissions } from '@practium/features/permissions';
import {
	LANDING_ROUTES,
	MOCK_LANDING_PLANS,
	MOCK_PLAN_FORM_DATA,
	DEFAULT_PLAN_FORM,
} from '../constants/landing.constants';
import styles from './ViewLandingPlanPage.module.css';

export const ViewLandingPlanPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { abilities } = usePermissions();
	const canEdit = abilities.can('update', 'landingPlans');
	const canDelete = abilities.can('delete', 'landingPlans');
	const id = searchParams.get('id') ?? '';

	const mockPlan = MOCK_LANDING_PLANS.find(p => p._id === id);
	const resolvedInitialData = MOCK_PLAN_FORM_DATA[id] ?? {
		...DEFAULT_PLAN_FORM,
		name: mockPlan?.name ?? '',
	};
	const activePlanCount = MOCK_LANDING_PLANS.filter(
		p => p._id !== id && p.status === 'active'
	).length;

	const [currentMode, setCurrentMode] = useState<FormPageMode>('view');
	const [isLoading, setIsLoading] = useState(false);
	const [liveData, setLiveData] = useState<PlanFormData>(resolvedInitialData);
	const [liveFormData, setLiveFormData] = useState<PlanFormData>(resolvedInitialData);

	const planRef = useRef<PlanFormRef>(null);

	const { success: toastSuccess, error: toastError } = useToast();

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const handleSubmit = useCallback(() => {
		setCurrentMode('view');
	}, []);

	const handleFormChange = useCallback((data: PlanFormData) => {
		setLiveFormData(data);
	}, []);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			setCurrentMode('view');
			setLiveData(resolvedInitialData);
			setLiveFormData(resolvedInitialData);
		};
		if (!planRef.current?.isDirty?.()) {
			revert();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [confirm, resolvedInitialData]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Remover plano da landing page',
			message:
				'Tem certeza que deseja remover este plano da landing page? Esta ação não pode ser desfeita.',
			confirmText: 'Remover',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		try {
			await removeLandingPlan(id);
			toastSuccess('Plano removido da landing page');
			navigate(LANDING_ROUTES.CONTROL);
		} catch {
			toastError('Erro ao remover plano da landing page');
		}
	}, [confirm, id, navigate, toastSuccess, toastError]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);

	const handleSave = useCallback(() => {
		const wasActive = resolvedInitialData.isListable === 'yes';
		const willBeActive = liveFormData.isListable === 'yes';
		if (!wasActive && willBeActive && activePlanCount >= 3) {
			toastError(
				'Não é possível ativar: já existem 3 planos na landing page. Remova um antes de ativar outro.'
			);
			return;
		}
		planRef.current?.submit();
	}, [resolvedInitialData.isListable, liveFormData.isListable, activePlanCount, toastError]);

	return (
		<div className={styles.page}>
			<PageHeader
				title={currentMode === 'view' ? 'Informações do Plano' : 'Editar Plano'}
				subtitle={
					currentMode === 'view'
						? 'Visualize os dados gerais do plano'
						: 'Edite os dados gerais do plano'
				}
				onBack={() => navigate(LANDING_ROUTES.CONTROL)}
			/>
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Gerais"
						className={styles.mainCard}
					>
						<PlanForm
							ref={planRef}
							mode={currentMode}
							initialData={liveData}
							onSubmit={handleSubmit}
							onLoadingChange={setIsLoading}
							onFormChange={handleFormChange}
						/>
					</FormCard>
					<div className={styles.pageActions}>
						<FormActions
							mode={currentMode}
							onCancel={handleCancel}
							onSave={handleSave}
							isLoading={isLoading}
							onDelete={canDelete ? handleDelete : undefined}
							onEdit={canEdit ? handleEdit : undefined}
						/>
					</div>
				</div>
				<div className={styles.sideCard}>
					<FormCard icon={<Preview />} title="Preview">
						<div className={styles.formPreviewCard}>
							<PlanPreviewCard name={liveFormData.name} advantages={liveFormData.advantages} />
						</div>
					</FormCard>
				</div>
			</div>
			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
